import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormDropdownMenu from "../../components/form/FormDropdownMenu";
import FormList from "../../components/form/FormList";
import FormListButtonWrapper from "../../components/form/FormListButtonWrapper";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorRow from "../../components/inspector/InspectorRow";
import { INVERSE_ID_PREFIX } from "../../constants/idPrefixes";
import interfaceUserActions from "../../constants/interfaceUserActions";
import { appStateOperations } from "../../modules/appState";
import { batchActions } from "../../modules/chainReducer";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { relationshipOperations } from "../../modules/relationships";
import { uiStateOperations } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { trackEvent } from "../../utils/trackAnalytics";
import renderNotAllowedBox from "./renderNotAllowedBox";
import selectors from "./selectors";

class InspectorRelationshipTab extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      addedRelationshipId: null,
      addedRelationshipIsInverse: false,
      showMoreRelationships: false
    };

    this.onAddRelationship = this.onAddRelationship.bind(this);
    this.onDeleteRelationship = this.onDeleteRelationship.bind(this);
    this.getRelationshipOptionsByItemType = this.getRelationshipOptionsByItemType.bind(this);
    this.toggleFormList = this.toggleFormList.bind(this);
    this.toggleFormDropdown = this.toggleFormDropdown.bind(this);
    this.toggleShowMore = this.toggleShowMore.bind(this);
    this.onRelationshipOptionClick = this.onRelationshipOptionClick.bind(this);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.addedRelationshipId && !prevState.addedRelationshipId) {
      const domNode = document.getElementById(
        `inspectorRelationshipList-${this.state.addedRelationshipIsInverse ? INVERSE_ID_PREFIX : ""}${
          this.state.addedRelationshipId
        }`
      );
      if (domNode) {
        domNode.click();
      }
    }
  }

  getCombinedReferences = () => {
    const { itemData, typeData, relationshipOptionsByItemType, referenceAllIds } = this.props;
    const { addedRelationshipId, addedRelationshipIsInverse } = this.state;

    const references = typeData.references.map(reference => {
      const relationships = itemData.relationshipsByRefType.has(reference.id)
        ? itemData.relationshipsByRefType
          .get(reference.id)
          .groupBy(r => r.id + r.subject + r.object)
          .map(r => r.first())
          .toList()
        : List([]);

      const values = relationships
        .map(v => {
          return {
            label: getLabelWithUntitled(v.objectLabel),
            subLabel: v.objectAge,
            value: v.object
          };
        })
        .toArray();

      if (
        !values.length &&
        !reference.showInInspector &&
        (addedRelationshipId != reference.id || addedRelationshipIsInverse)
      ) {
        return null;
      }

      let options = [];
      reference.types.forEach(typeId => {
        const typeOptions = relationshipOptionsByItemType.get(typeId);
        if (typeOptions) {
          options = _.union(options, typeOptions.toJS());
        }
      });

      return {
        value: reference.id,
        label: reference.label,
        values: values,
        relationships: relationships,
        options: options
        // inverse: false
      };
    });

    const inverseReferences = typeData.inverseReferences.map(reference => {
      // Don't show duplicate bidirectional relationships
      if (typeData.references.find(r => r.id === reference.id && r.bidirectional)) {
        return null;
      }

      const relationships = itemData.inverseRelationshipsByRefType.has(reference.id)
        ? itemData.inverseRelationshipsByRefType
          .get(reference.id)
          .groupBy(r => r.id + r.subject + r.object)
          .map(r => r.first())
          .toList()
        : List([]);

      const values = relationships
        .map(v => {
          return {
            label: getLabelWithUntitled(v.objectLabel),
            subLabel: v.objectAge,
            value: v.subject
          };
        })
        .toArray();

      if (
        !values.length &&
        !reference.showInInspector &&
        (addedRelationshipId != reference.id || !addedRelationshipIsInverse)
      ) {
        return null;
      }

      let options = [];
      reference.inverseTypes.forEach(typeId => {
        const typeOptions = relationshipOptionsByItemType.get(typeId);
        if (typeOptions) {
          options = _.union(options, typeOptions.toJS());
        }
      });

      return {
        value: `${INVERSE_ID_PREFIX}${reference.id}`,
        label: reference.inverseLabel ? reference.inverseLabel : reference.label,
        values: values,
        relationships: relationships,
        options: options
        // inverse: true
      };
    });

    const combinedReferences = references
      .concat(inverseReferences)
      .filter(ref => ref)
      .sort((a, b) => {
        const aId = a.value.indexOf(INVERSE_ID_PREFIX) > -1 ? a.value.substr(INVERSE_ID_PREFIX.length) : a.value;
        const bId = b.value.indexOf(INVERSE_ID_PREFIX) > -1 ? b.value.substr(INVERSE_ID_PREFIX.length) : b.value;
        const aIndex = referenceAllIds.indexOf(aId);
        const bIndex = referenceAllIds.indexOf(bId);
        return aIndex < bIndex ? -1 : aIndex > bIndex ? 1 : 0;
      });

    return combinedReferences;
  };

  renderReference = reference => {
    const { itemData } = this.props;
    const listId = `inspectorRelationshipList-${reference.value}`;

    return reference.options.length > 0 ? (
      <InspectorRow key={`${reference.value}`} label={reference.label}>
        <InspectorField>
          <FormList
            id={listId}
            values={reference.values}
            options={reference.options}
            canAdd={true}
            canDelete={true}
            allowSearch={true}
            onAddItem={itemId => this.onAddRelationship(reference.value, itemId)}
            onDeleteItem={itemId => this.onDeleteRelationship(reference.value, itemId)}
            onItemClick={itemId => this.props.onItemClick(itemId)}
            title={reference.label}
            backText={getLabelWithUntitled(itemData.label)}
            onToggleMenu={this.toggleFormList}
          />
        </InspectorField>
      </InspectorRow>
    ) : null;
  };

  onAddRelationship(refId, itemId) {
    const isInverse = refId.indexOf(INVERSE_ID_PREFIX) > -1;
    const referenceId = !isInverse ? refId : refId.substr(INVERSE_ID_PREFIX.length);

    if (!isInverse) {
      this.props.onCreateRelationship(this.props.itemIds.toArray(), referenceId, [itemId]);
    } else {
      this.props.onCreateRelationship([itemId], referenceId, this.props.itemIds.toArray());
    }
  }

  onDeleteRelationship(refId, itemId) {
    const isInverse = refId.indexOf(INVERSE_ID_PREFIX) > -1;
    const referenceId = !isInverse ? refId : refId.substr(INVERSE_ID_PREFIX.length);

    if (!isInverse) {
      this.props.onDeleteRelationship(this.props.itemIds.toArray(), referenceId, [itemId]);
    } else {
      this.props.onDeleteRelationship([itemId], referenceId, this.props.itemIds.toArray());
    }
  }

  toggleFormList(menuOpen) {
    if (!menuOpen) {
      this.setState({ addedRelationshipId: null, addedRelationshipIsInverse: false });
    }
  }

  toggleFormDropdown(menuOpen) {
    if (!menuOpen) {
      this.setState({ showMoreRelationships: false });
    }
  }

  getRelationshipOptionsByItemType() {
    const itemTypes = this.props.sortedItemTypes;
    return itemTypes.map(type => {
      let refOptions = this.props.typeData.references
        .filter(ref => ref.types.includes(type.id))
        .map(ref => {
          return {
            value: ref.id,
            label: ref.label
          };
        });
      let inverseRefOptions = this.props.typeData.inverseReferences
        .filter(
          ref =>
            ref.inverseTypes.includes(type.id) &&
            !this.props.typeData.references.find(ref2 => ref.bidirectional && ref.id === ref2.id)
        )
        .map(ref => {
          return {
            value: `${INVERSE_ID_PREFIX}${ref.id}`,
            label: ref.showInverse && ref.inverseLabel ? ref.inverseLabel : ref.label
            // inverse: true
          };
        });
      refOptions = refOptions.concat(inverseRefOptions).toArray();

      return {
        label: type.label,
        value: "event-" + type.id,
        children: !type.disabled ? refOptions : null,
        disabled: type.disabled
      };
    });
  }

  toggleShowMore() {
    this.setState({
      showMoreRelationships: !this.state.showMoreRelationships
    });
  }

  onRelationshipOptionClick(val) {
    const isInverse = val.indexOf(INVERSE_ID_PREFIX) > -1;
    const referenceId = !isInverse ? val : val.substr(INVERSE_ID_PREFIX.length);
    this.setState({ addedRelationshipId: referenceId, addedRelationshipIsInverse: isInverse }, () => {
      this.toggleShowMore();
    });
  }

  render() {
    const isIos = preprocessorQuery.isIos();
    const relationshipOptionsByItemType = this.getRelationshipOptionsByItemType();

    const allowsRelationships =
      this.props.typeData.references.size > 0 || this.props.typeData.inverseReferences.size > 0;

    const combinedReferences = this.getCombinedReferences();
    const combinedReferenceNodes = combinedReferences.map(ref => this.renderReference(ref)).toArray();

    return allowsRelationships ? (
      <InspectorGroup label={!isIos ? "Relationships" : null}>
        {combinedReferenceNodes}
        <FormListButtonWrapper onClick={this.toggleShowMore}>
          <div id="addMoreRelationships" className="tertiaryBtn">
            <svg className={"icon"}>
              <use xlinkHref="#listAdd" />
            </svg>
            {combinedReferences.size ? "More Relationships" : "Add Relationships"}
          </div>
        </FormListButtonWrapper>
        <FormDropdownMenu
          targetId="addMoreRelationships"
          showMenu={this.state.showMoreRelationships}
          options={relationshipOptionsByItemType}
          onOptionClick={this.onRelationshipOptionClick}
          onToggleMenu={this.toggleFormDropdown}
          allowSearch={true}
          focusOnOpen={true}
        />
      </InspectorGroup>
    ) : (
      renderNotAllowedBox("Relationships", "relationships", this.props.typeData.label, this.props.onOpenSettingsClick)
    );
  }
}

InspectorRelationshipTab.propTypes = {
  onCreateRelationship: PropTypes.func.isRequired,
  onDeleteRelationship: PropTypes.func.isRequired,
  onChangeRelationship: PropTypes.func.isRequired,
  onOpenSettingsClick: PropTypes.func.isRequired,
  itemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemData: PropTypeShapes.inspectorItem,
  typeData: PropTypeShapes.denormalisedTypeDefinition,
  relationshipOptionsByItemType: PropTypeShapes.listOptionListByIdMap,
  sortedItemTypes: ImmutablePropTypes.list,
  onItemClick: PropTypes.func.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  itemIdsByType: ImmutablePropTypes.map.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  onDeleteItemRelationships: PropTypes.func.isRequired,
  onSetHasChildPopover: PropTypes.func,
  referenceAllIds: ImmutablePropTypes.list.isRequired
};

const mapStateToProps = state => {
  return {
    relationshipOptionsByItemType: selectors.getRelationshipOptionsByItemType(state.present),
    sortedItemTypes: selectors.getSortedItemTypes(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    itemIdsByType: itemSelectors.getItemIdsByType(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    referenceAllIds: referenceSelectors.getReferenceAllIds(state.present)
  };
};

const readonlyActions = {
  onOpenSettingsClick: uiStateOperations.showSettingsModal,
  onItemClick: appStateOperations.selectItems
};

const editingActions = {
  onCreateRelationship: (subjectIds, referenceId, itemId) => {
    trackEvent("createItemRelationshipFromInspector", "setItemRelationship", referenceId);
    const actions = [relationshipOperations.createRelationship(subjectIds, referenceId, itemId)];
    actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.CREATE_RELATIONSHIP_INSPECTOR));
    return batchActions(actions);
  },
  onDeleteRelationship: (subjectIds, referenceId, itemIds) => {
    trackEvent("deleteItemRelationshipFromInspector", "setItemRelationship", referenceId);
    return relationshipOperations.deleteRelationshipsWithValues(subjectIds, referenceId, itemIds);
  },
  onChangeRelationship: (subjectIds, referenceId, itemId) => {
    if (itemId) {
      return relationshipOperations.createRelationship(subjectIds, referenceId, itemId);
    } else {
      return relationshipOperations.deleteRelationshipsWithValues(subjectIds, referenceId, null);
    }
  },
  onDeleteItemRelationships: (itemIds, itemRelationships) => {
    trackEvent("deleteItemRelationshipsFromInspector", "setItemRelationship");
    let actions = [];
    itemRelationships.subValues.forEach(rel => {
      if (!rel.inverse) {
        actions.push(
          relationshipOperations.deleteRelationshipsWithValues(itemIds.toArray(), rel.value, [itemRelationships.value])
        );
      } else {
        actions.push(
          relationshipOperations.deleteRelationshipsWithValues([itemRelationships.value], rel.value, itemIds.toArray())
        );
      }
    });
    return batchActions(actions);
  },
  onSetHasChildPopover: uiStateOperations.setLeftPanelHasChildPopover
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InspectorRelationshipTab);
